import useAsyncState from '@swarm/core/hooks/async/useAsyncState'
import useTransactionAlerts from '@swarm/core/hooks/i18n/useTransactionAlerts'
import { useSnackbar } from '@swarm/ui/swarm/Snackbar'
import { Address } from '@web3-onboard/core/dist/types'
import { BigSource } from 'big.js'
import { useCallback } from 'react'
import 'react-datepicker/dist/react-datepicker.css'

import { XDotcContract, XDotcContractToken } from 'src/contracts/XDotcContract'

type UseEditOfferState = {
  txLoading: boolean
}

type EditOfferFunction = (
  offerId: string,
  tokenOut: XDotcContractToken,
  amountOut: BigSource,
  expiresAt: number,
  isFullType: boolean,
  specialAddresses: Address[],
  timelock: number,
  terms: string,
  commsLink: string,
) => Promise<void>

type UseEditOfferTokenReturn = [EditOfferFunction, UseEditOfferState]

const useEditOffer = (): UseEditOfferTokenReturn => {
  const { track } = useTransactionAlerts()
  const { addError } = useSnackbar()
  const [txLoading, setTxLoading] = useAsyncState(false)

  const editOffer: EditOfferFunction = useCallback(
    async (
      offerId,
      tokenOut,
      amountOut,
      expiresAt,
      isFullType,
      specialAddresses,
      timelock,
      terms,
      commsLink,
    ) => {
      setTxLoading(true)
      try {
        const tx = await XDotcContract.editOffer(
          offerId,
          tokenOut,
          amountOut,
          expiresAt,
          timelock,
          isFullType,
          specialAddresses,
          terms,
          commsLink,
        )

        await track(tx, {
          confirm: {
            secondaryMessage: 'Refresh shortly to see the latest offers.',
          },
        })
      } catch (e) {
        console.log(e)
        addError(e as Error)
      } finally {
        setTxLoading(false)
      }
    },
    [setTxLoading, track, addError],
  )

  return [editOffer, { txLoading }]
}

export default useEditOffer
